import React, { useContext } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faCog, faMoon, faSignOutAlt, faHistory, faExchangeAlt, faFolderOpen, faComments, faUser } from '@fortawesome/free-solid-svg-icons';
import { SettingsContext } from '../contexts/SettingsContext';
import { auth, signOut } from '../firebase';

const HeaderContainer = styled.header`
  background: rgba(255, 255, 255, 0.05);
  color: var(--text-color);
  padding: var(--space-4) var(--space-6);
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 10px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  position: relative;
  z-index: 10;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
`;

const Title = styled.h1`
  margin: 0;
  font-size: var(--text-xl);
  font-weight: 500;
  font-family: var(--font-heading-bold);
  color: var(--text-color);
  letter-spacing: -0.01em;
  line-height: 1.4;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  cursor: pointer;
  padding: var(--space-2);
  border-radius: var(--radius-lg);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: -40px;
    left: 50%;
    transform: translateX(-50%) scale(0.95);
    background-color: var(--neutral-800);
    color: var(--neutral-100);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-md);
    font-size: var(--text-xs);
    font-weight: 500;
    white-space: nowrap;
    z-index: 1000;
    opacity: 0;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: none;
    box-shadow: var(--shadow-lg);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }

  &:hover {
    background-color: var(--primary-50);
    color: var(--primary-700);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }

  &:hover::after {
    transform: translateX(-50%) scale(1);
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }

  &:active {
    transform: translateY(0) scale(0.98);
    background-color: var(--primary-100);
    box-shadow: var(--shadow-sm);
  }

  .dark-mode & {
    &:hover {
      background-color: var(--primary-100);
      color: var(--primary-600);
    }
    &:active {
      background-color: var(--primary-200);
    }
    &::after {
      background-color: var(--neutral-700);
      color: var(--neutral-900);
    }
  }
`;

const Header = ({ toggleSidebar, isSidebarOpen, openSettings, toggleTheme, isDarkMode, openLog, onNavigate, openProfile }) => {
  const { toggleTextDirection } = useContext(SettingsContext);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  return (
    <HeaderContainer>
      <HeaderLeft>
        <IconButton onClick={toggleSidebar} data-tooltip="Toggle sidebar" aria-label="Toggle sidebar">
          <FontAwesomeIcon icon={faBars} />
        </IconButton>
        <Title>SuperChat</Title>
      </HeaderLeft>
      <HeaderRight>
        <IconButton onClick={() => onNavigate('chat')} data-tooltip="Chat" aria-label="Chat">
          <FontAwesomeIcon icon={faComments} />
        </IconButton>
        <IconButton onClick={() => onNavigate('projects')} data-tooltip="Projects" aria-label="Projects">
          <FontAwesomeIcon icon={faFolderOpen} />
        </IconButton>
        <IconButton onClick={toggleTextDirection} data-tooltip="Toggle text direction" aria-label="Toggle text direction">
          <FontAwesomeIcon icon={faExchangeAlt} />
        </IconButton>
        <IconButton onClick={openLog} data-tooltip="View history" aria-label="View history">
          <FontAwesomeIcon icon={faHistory} />
        </IconButton>
        <IconButton onClick={openProfile} data-tooltip="User profile" aria-label="User profile">
          <FontAwesomeIcon icon={faUser} />
        </IconButton>
        <IconButton onClick={openSettings} data-tooltip="Open settings" aria-label="Open settings">
          <FontAwesomeIcon icon={faCog} />
        </IconButton>
        <IconButton onClick={toggleTheme} data-tooltip="Toggle theme" aria-label="Toggle theme">
          <FontAwesomeIcon icon={faMoon} />
        </IconButton>
        <IconButton onClick={handleLogout} data-tooltip="Logout" aria-label="Logout">
          <FontAwesomeIcon icon={faSignOutAlt} />
        </IconButton>
      </HeaderRight>
    </HeaderContainer>
  );
};

export default Header;